// @ts-nocheck
/**
 * Ludipek B2B draft-order submission.
 *
 * Approved B2B customers don't reach native Shopify checkout. Clicking "Enviar pedido a
 * Ludipek" on the cart page posts the HMAC-signed cart attributes (already written by
 * b2b-cart-snapshot.js) to {apiBaseUrl}/orders/draft. The API creates a Shopify Draft
 * Order via the Admin API with the signed B2B prices baked into the line items; sales
 * reviews in Shopify admin, then confirms to NetSuite.
 *
 * Runs only when an NS customer ID is resolvable from the currently selected CompanyLocation.
 * Logged-out + pending paths are handled by b2b-cart-snapshot.js (checkout guard) and
 * b2b-pending-gate.js.
 *
 * Ordering note: this script defers loading, so b2b-cart-snapshot.js runs first and
 * installs its capture-phase submit listener on form#cart-form. Our bubble-phase click
 * handler on the submit button runs AFTER that guard, so by the time we fire, the sign
 * is guaranteed to be fresh (or the submit would have been aborted with an error).
 */
(function () {
  const BOOTSTRAP = window.__LUDIPEK_B2B__ || null;
  if (BOOTSTRAP === null) return;
  if (BOOTSTRAP.isPending === true) return;

  // The NS customer ID for draft-order creation comes from the buyer's currently selected
  // CompanyLocation — same source the cart signing uses. Resolved lazily so a mid-session
  // location switch is honoured without a page reload.
  function currentCustomerId() {
    return window.LudipekB2BLocation ? window.LudipekB2BLocation.getCurrentNetsuiteCustomerId() : null;
  }

  function getEffectiveCountry() {
    if (window.LudipekB2BLocation && window.LudipekB2BLocation.getEffectiveCountry) {
      return window.LudipekB2BLocation.getEffectiveCountry();
    }
    return BOOTSTRAP ? BOOTSTRAP.storeCountry : null;
  }

  if (currentCustomerId() == null) return;

  const FETCH_TIMEOUT_MS = 15_000;
  const CONFIRMATION_ID = 'b2b-draft-confirmation';
  const IDEMPOTENCY_KEY_STORAGE = 'ludipek:draft_idempotency_key';

  const snapshot = () => window.LudipekB2BCartSnapshot || null;

  function logDebug(message, details) {
    if (details === undefined) console.debug('[b2b-draft-order]', message);
    else console.debug('[b2b-draft-order]', message, details);
  }

  function logError(message, details) {
    if (details === undefined) console.error('[b2b-draft-order]', message);
    else console.error('[b2b-draft-order]', message, details);
  }

  function message(key, fallback) {
    const messages = BOOTSTRAP.messages || {};
    return messages[key] || fallback;
  }

  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async function readCart() {
    const response = await fetchWithTimeout('/cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      throw new Error(`GET /cart.js failed with HTTP ${response.status}`);
    }
    return response.json();
  }

  function buildIdempotencyKey() {
    // One key per in-progress submit attempt. We persist across the network call so
    // that an accidental double-click or reload retries the same request; the API
    // should cache and return the original draft id on replay.
    const existing = sessionStorage.getItem(IDEMPOTENCY_KEY_STORAGE);
    if (existing !== null) return existing;
    const key =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(IDEMPOTENCY_KEY_STORAGE, key);
    return key;
  }

  function clearIdempotencyKey() {
    sessionStorage.removeItem(IDEMPOTENCY_KEY_STORAGE);
  }

  async function clearShopifyCart() {
    // /cart/clear.js removes items + note but NOT attributes — we must wipe the stale
    // b2b_payload / b2b_sig / b2b_sig_alg explicitly so the next build-up signs clean.
    try {
      await fetchWithTimeout('/cart/clear.js', { method: 'POST' });
      await fetchWithTimeout('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: { b2b_payload: '', b2b_sig: '', b2b_sig_alg: '' },
        }),
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      logError('cart clear failed (draft already created, ignoring)', { error: String(error) });
    }
  }

  function renderConfirmation(reference) {
    const container =
      document.querySelector('main#MainContent') ||
      document.querySelector('main') ||
      document.body;
    const existing = document.getElementById(CONFIRMATION_ID);
    if (existing !== null) existing.remove();
    const wrapper = document.createElement('section');
    wrapper.id = CONFIRMATION_ID;
    wrapper.setAttribute('role', 'status');
    wrapper.style.cssText =
      'max-width:640px;margin:48px auto;padding:32px;text-align:center;' +
      'border:1px solid rgb(var(--color-foreground-rgb,0 0 0) / 0.12);' +
      'border-radius:8px;background:rgb(var(--color-foreground-rgb,0 0 0) / 0.03);';
    const title = document.createElement('h2');
    title.textContent = message('draftSuccessTitle', 'Order sent!');
    title.style.cssText = 'margin:0 0 12px;font-size:1.5rem;';
    const body = document.createElement('p');
    const template = message(
      'draftSuccessBodyHtml',
      'Your order <b>#__REF__</b> was received. Our team will review and confirm within 1 business day.'
    );
    body.innerHTML = template.replace('__REF__', escapeHtml(reference));
    body.style.cssText = 'margin:0;font-size:1rem;line-height:1.5;';
    wrapper.appendChild(title);
    wrapper.appendChild(body);
    container.innerHTML = '';
    container.appendChild(wrapper);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value);
    return div.innerHTML;
  }

  function findForm() {
    return document.querySelector('form#cart-form, form[action="/cart"]');
  }

  function findSubmitButton(form) {
    if (form === null) return null;
    return form.querySelector('button[data-b2b-draft-submit]');
  }

  async function submitDraftOrder(form, button) {
    const helpers = snapshot();
    const inflight = helpers ? helpers.getInflightPromise() : null;
    if (inflight !== null && inflight !== undefined) {
      logDebug('waiting for in-flight sign before submit');
      try {
        await inflight;
      } catch (error) {
        if (helpers) {
          helpers.showBanner(
            form,
            message('checkoutSignFailed', 'We could not verify your B2B prices.')
          );
        }
        return;
      }
    }

    let cart;
    try {
      cart = await readCart();
    } catch (error) {
      logError('failed to read cart', { error: String(error) });
      if (helpers) helpers.showBanner(form, message('draftFailed', 'We could not send your order.'));
      return;
    }

    const attributes = cart.attributes || {};
    const b2bPayload = attributes.b2b_payload;
    const b2bSig = attributes.b2b_sig;
    const b2bSigAlg = attributes.b2b_sig_alg;
    if (!b2bPayload || !b2bSig || !b2bSigAlg) {
      logError('missing signed cart attributes');
      if (helpers) helpers.showBanner(form, message('checkoutSignFailed', 'We could not verify your B2B prices.'));
      return;
    }

    const url = `${BOOTSTRAP.apiBaseUrl.replace(/\/$/, '')}/orders/draft`;
    const body = {
      store_country: getEffectiveCountry(),
      customer_id: currentCustomerId(),
      b2b_payload: b2bPayload,
      b2b_sig: b2bSig,
      b2b_sig_alg: b2bSigAlg,
      cart_note: cart.note || '',
      cart_attributes: attributes,
    };
    const idempotencyKey = buildIdempotencyKey();

    if (helpers) helpers.setButtonBusy(button, true);
    if (helpers) helpers.hideBanner();

    let response;
    try {
      // Skip AbortController here: the `keepalive: true` flag lets the POST survive a
      // page unload (important if the user navigates away mid-submit), and some browsers
      // silently drop `keepalive` when a signal is present. The API is expected to respond
      // within its own timeout; if it hangs, the user sees the busy button and can retry.
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'x-api-key': BOOTSTRAP.apiShopifyKey,
        },
        body: JSON.stringify(body),
        keepalive: true,
      });
    } catch (error) {
      logError('POST /orders/draft failed (network)', { error: String(error) });
      if (helpers) helpers.setButtonBusy(button, false);
      if (helpers) helpers.showBanner(form, message('draftFailed', 'We could not send your order.'));
      return;
    }

    if (response.status === 409) {
      clearIdempotencyKey();
      if (helpers) helpers.setButtonBusy(button, false);
      if (helpers) helpers.showBanner(form, message('draftConflict', 'Your cart changed. Please refresh.'));
      return;
    }
    if (!response.ok) {
      logError(`POST /orders/draft failed with HTTP ${response.status}`);
      if (helpers) helpers.setButtonBusy(button, false);
      if (helpers) helpers.showBanner(form, message('draftFailed', 'We could not send your order.'));
      return;
    }

    let data;
    try {
      data = await response.json();
    } catch (error) {
      data = {};
    }
    const reference = data.reference || data.draft_order_id || '—';
    logDebug(`draft order created #${reference}`);

    clearIdempotencyKey();
    await clearShopifyCart();
    renderConfirmation(reference);
  }

  function installSubmitHandler() {
    const form = findForm();
    if (form === null) {
      logDebug('no cart form on this page — skipping');
      return;
    }
    const button = findSubmitButton(form);
    if (button === null) {
      logDebug('no data-b2b-draft-submit button — skipping (not a B2B cart page)');
      return;
    }

    // Bubble-phase click on the button — b2b-cart-snapshot.js owns the capture-phase
    // submit guard on the form itself, so by the time this click fires, the signature
    // is either fresh or the submit was already aborted.
    button.addEventListener('click', async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      await submitDraftOrder(form, button);
    });
    logDebug('draft-order submit handler installed');
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  onReady(installSubmitHandler);
})();
